import { useRef, useState } from 'react'
import type { FocusEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { BarChart2, ChevronDown, Layers } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { cn } from '@/lib/utils'
import { ROUTES } from '@/app/routes'
import { useActiveTopic, setLastViewedTopicId } from '../state/useActiveTopic'
import { fetchTopics } from '../api/topicsClient'

const TOPIC_LIST_QUERY = {
  page: 1,
  limit: 20,
  platform: 'all',
  search: '',
  sort: '-updatedAt',
} as const

export function TopicSwitcher() {
  const { t } = useTranslation('topbar')
  const { topicId } = useActiveTopic()
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const { data } = useQuery({
    queryKey: ['topics', TOPIC_LIST_QUERY],
    queryFn: ({ signal }) => fetchTopics(TOPIC_LIST_QUERY, signal),
  })

  const topics = data?.data ?? []

  const activeTopic = topics.find((topic) => String(topic.id) === String(topicId)) ?? null

  const handleSelect = (id: string) => {
    setLastViewedTopicId(id)
    setOpen(false)
    navigate(ROUTES.app.topicFeed(id))
  }

  const handleToggle = () => setOpen((prev) => !prev)

  const handleBlur = (e: FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setOpen(false)
    }
  }

  return (
    <div ref={containerRef} className="relative" onBlur={handleBlur}>
      <button
        type="button"
        onClick={handleToggle}
        className={cn(
          'flex h-9 max-w-[200px] items-center gap-1.5 rounded-lg border border-border bg-muted/40 px-3 text-sm transition-colors hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring',
          open && 'bg-muted',
        )}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <Layers size={14} strokeWidth={2} className="shrink-0 text-muted-foreground" />
        <span className="truncate font-medium">
          {activeTopic ? activeTopic.name : t('switcher.placeholder')}
        </span>
        <ChevronDown
          size={14}
          strokeWidth={2}
          className={cn(
            'shrink-0 text-muted-foreground transition-transform duration-150',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full z-50 mt-1 min-w-[200px] max-w-[280px] rounded-lg border border-border bg-popover shadow-lg"
        >
          {topics.length > 0 && (
            <ul className="max-h-60 overflow-y-auto py-1">
              {topics.map((topic) => (
                <li key={topic.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={String(topic.id) === String(topicId)}
                    onClick={() => handleSelect(String(topic.id))}
                    className={cn(
                      'flex w-full items-center px-3 py-2 text-left text-sm transition-colors hover:bg-muted',
                      String(topic.id) === String(topicId)
                        ? 'font-semibold text-foreground'
                        : 'text-muted-foreground',
                    )}
                  >
                    <span className="truncate">{topic.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="border-t border-border px-1 py-1">
            <Link
              to={ROUTES.app.topicsList}
              onClick={() => setOpen(false)}
              className="flex w-full items-center rounded px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              {t('switcher.manage')}
            </Link>
            <Link
              to={ROUTES.app.trends}
              onClick={() => setOpen(false)}
              className="flex w-full items-center gap-1.5 rounded px-2 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <BarChart2 size={13} strokeWidth={2} />
              {t('switcher.trendIndex')}
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
